import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  Box,
  Container,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Fab,
  Zoom,
  useMediaQuery,
  useTheme,
  Paper,
  Chip,
  Breadcrumbs,
  Link,
  Button,
  Stack,
} from '@mui/material'
import {
  useParams,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import TaxTreatySideBar from './TaxTreatySideBar'
import TaxTreatyCard from './TaxTreatyCard'
import Pagination from '../Pagination'
import ItemsPerPageSelector from '../ItemsPerPageSelector'
// import usePageTracking from '../../services/usePageTracking'
import CustomSnackbar from '../CustomSnackbar'
import NoResultsModal from '../NoResultsModal'
import apiService from '../../services/ApiService'
import { activityTracker } from '../../services/ActivityTracker'
import { useAuth } from '../../context/AuthContext'

const normalizePartnerCountryParam = (value) => {
  if (typeof value !== 'string') {
    return ''
  }
  const trimmed = value.trim()
  if (!trimmed) {
    return ''
  }
  if (trimmed.toLowerCase() === 'all partner countries') {
    return ''
  }
  return trimmed
}
/**
 * TaxTreatyList component for displaying and searching tax treaties
 */
const TaxTreatyList = ({
  useExactSearch = false,
  autocompleteEnabled = true,
} = {}) => {
  // usePageTracking()
  const MemoizedPagination = React.memo(Pagination)
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [allSearchResults, setAllSearchResults] = useState([])
  // React Router hooks
  const { country: urlCountry } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Get query parameters
  const partnerCountryParam = normalizePartnerCountryParam(
    searchParams.get('partner')
  )
  const queryParam = searchParams.get('query')

  // State for filters
  const [primaryCountry, setPrimaryCountry] = useState(urlCountry || '')
  const [selectedCountry, setSelectedCountry] = useState(
    partnerCountryParam || ''
  )
  const [searchQuery, setSearchQuery] = useState(queryParam || '')
  const [hasSearched, setHasSearched] = useState(Boolean(queryParam))

  // Search state
  const searchRequestRef = useRef(null)
  const [pendingFilterChanges, setPendingFilterChanges] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [totalItems, setTotalItems] = useState(0)
  const [currentPageData, setCurrentPageData] = useState([])

  // UI state
  const [loading, setLoading] = useState(false)
  const [isInitializing, setIsInitializing] = useState(true)
  const [error, setError] = useState(null)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [availableCountries, setAvailableCountries] = useState([])
  const [showFilters, setShowFilters] = useState(!isMobileScreen)
  const [showBackToTop, setShowBackToTop] = useState(false)
  // No-results modal state — opens after a search that returns zero
  // treaties (after v2 and any v1 fallback), so the user can adjust filters.
  const [showNoResultsModal, setShowNoResultsModal] = useState(false)
  // Snapshot the query + scope at search-response time so the modal copy
  // stays stable even if the user edits filters afterwards.
  const [noResultsSnapshot, setNoResultsSnapshot] = useState({
    query: '',
    scopeName: '',
  })

  // Skip unnecessary API calls due to media queries changing
  const prevScreenSize = useRef(isSmallScreen)
  const lastFetchTimestamp = useRef(0)
  const debouncedSearchTimeout = useRef(null)
  const hasSettledInitializationRef = useRef(false)

  const { isAuthenticated, user } = useAuth()

  // Theme colors
  const themeColors = useMemo(
    () => ({
      primary: '#232536',
      secondary: '#ffcf51',
      hover: '#3a3e5a',
      border: '#e0e0e0',
    }),
    []
  )

  // Button radius
  const buttonBorderRadius = '4px' // '28px'

  /**
   * Update URL parameters
   */
  const updateUrlParams = useCallback(
    (directQuery = null) => {
      // Create new search params object
      const newSearchParams = new URLSearchParams()

      // Add partner country if selected
      if (selectedCountry) {
        newSearchParams.set('partner', selectedCountry)
      }

      // Add search query if provided
      if (directQuery !== null) {
        newSearchParams.set('query', directQuery)
      } else if (searchQuery) {
        newSearchParams.set('query', searchQuery)
      }

      // Update search params
      const nextSearch = newSearchParams.toString()
      if (nextSearch === searchParams.toString()) {
        return
      }
      setSearchParams(newSearchParams)
    },
    [selectedCountry, searchQuery, searchParams, setSearchParams]
  )

  /**
   * Handle search button click
   */
  const handleSearch = useCallback(
    (directQuery = null, selectedSuggestion = null) => {
      if (!primaryCountry) return

      // Clear any pending debounced search
      if (debouncedSearchTimeout.current) {
        clearTimeout(debouncedSearchTimeout.current)
        debouncedSearchTimeout.current = null
      }

      // Set search state
      setHasSearched(true)
      setPendingFilterChanges(false)
      setCurrentPage(1)

      // Use directQuery if provided, otherwise use state
      const queryToUse = directQuery !== null ? directQuery : searchQuery
      const normalizedSelectedSuggestion =
        typeof selectedSuggestion === 'string' &&
        selectedSuggestion.trim().length > 0
          ? selectedSuggestion.trim()
          : null

      // Update URL parameters
      updateUrlParams(queryToUse)

      // Store current search request
      const currentRequest = {
        primaryCountry,
        partnerCountry: selectedCountry,
        searchQuery: queryToUse,
        selectedSuggestion: normalizedSelectedSuggestion,
        timestamp: Date.now(),
      }

      searchRequestRef.current = currentRequest
      lastFetchTimestamp.current = currentRequest.timestamp
      // if (isAuthenticated) {
      //   activityTracker.trackCustomEvent('tax_treaty_search_initiated', {
      //     country1: primaryCountry,
      //     country2: selectedCountry || 'all',
      //     hasQuery: Boolean(queryToUse),
      //   })
      // }
      // Trigger API fetch
      setLoading(true)
    },
    [
      primaryCountry,
      selectedCountry,
      searchQuery,
      updateUrlParams,
      // , isAuthenticated
    ]
  )

  /**
   * Handle reset button click
   */
  const handleReset = useCallback(() => {
    // if (isAuthenticated) {
    //   activityTracker.trackCustomEvent('tax_treaty_search_reset', {
    //     country1: primaryCountry,
    //     country2: selectedCountry || 'all',
    //     hadQuery: Boolean(searchQuery),
    //   })
    // }
    // Reset search filters
    setSearchQuery('')
    setHasSearched(false)
    setCurrentPage(1)
    setPendingFilterChanges(false)

    // Update URL to remove parameters except partner country
    const newSearchParams = new URLSearchParams()
    if (selectedCountry) {
      newSearchParams.set('partner', selectedCountry)
    }
    setSearchParams(newSearchParams)

    // Trigger new search
    if (primaryCountry) {
      // Create new search request
      const currentRequest = {
        primaryCountry,
        partnerCountry: selectedCountry,
        searchQuery: '',
        timestamp: Date.now(),
      }

      searchRequestRef.current = currentRequest
      lastFetchTimestamp.current = currentRequest.timestamp

      setLoading(true)
    }
  }, [
    primaryCountry,
    selectedCountry,
    // searchQuery,
    setSearchParams,
    // isAuthenticated,
  ])

  // Monitor filter changes to set pendingFilterChanges flag
  useEffect(() => {
    // Skip during initialization
    if (isInitializing) {
      return
    }

    // Skip the first post-initialization effect run to avoid
    // triggering a duplicate search from URL/bootstrap state sync.
    if (!hasSettledInitializationRef.current) {
      return
    }

    if (primaryCountry) {
      setPendingFilterChanges(true)
    }
  }, [selectedCountry, isInitializing, primaryCountry])

  // Handle debounced filter changes
  useEffect(() => {
    // Only handle pending changes if not already loading
    if (pendingFilterChanges && !loading && primaryCountry) {
      // Clear any existing timeout
      if (debouncedSearchTimeout.current) {
        clearTimeout(debouncedSearchTimeout.current)
      }

      // Set new timeout to debounce API calls
      debouncedSearchTimeout.current = setTimeout(() => {
        setLoading(true)
        setPendingFilterChanges(false)
        updateUrlParams(searchQuery)
        setCurrentPage(1)

        const currentRequest = {
          primaryCountry,
          partnerCountry: selectedCountry,
          searchQuery,
          timestamp: Date.now(),
        }

        searchRequestRef.current = currentRequest
        lastFetchTimestamp.current = currentRequest.timestamp
        debouncedSearchTimeout.current = null
      }, 300) // 300ms debounce

      // Clean up timeout on unmount or if dependencies change
      return () => {
        if (debouncedSearchTimeout.current) {
          clearTimeout(debouncedSearchTimeout.current)
          debouncedSearchTimeout.current = null
        }
      }
    }
  }, [
    pendingFilterChanges,
    loading,
    primaryCountry,
    selectedCountry,
    searchQuery,
    updateUrlParams,
  ])

  // Handle itemsPerPage changes without triggering unnecessary searches
  useEffect(() => {
    if (
      !isInitializing &&
      primaryCountry &&
      !pendingFilterChanges &&
      searchRequestRef.current
    ) {
      setCurrentPage(1)

      // Only fetch if we don't have cached results
      if (allSearchResults.length === 0) {
        setLoading(true)
      }
    }
  }, [
    itemsPerPage,
    isInitializing,
    primaryCountry,
    pendingFilterChanges,
    allSearchResults.length,
  ])

  // Skip unnecessary renders when screen size changes
  useEffect(() => {
    if (prevScreenSize.current !== isSmallScreen) {
      prevScreenSize.current = isSmallScreen
      setShowFilters(!isMobileScreen)
    }
  }, [isSmallScreen, isMobileScreen])

  /**
   * Initialize component - fetch available countries
   */
  useEffect(() => {
    const initializeComponent = async () => {
      setIsInitializing(true)
      hasSettledInitializationRef.current = false

      try {
        // Reuse already-loaded countries to avoid refetching on query-param-only URL updates.
        const countries =
          availableCountries && availableCountries.length > 0
            ? availableCountries
            : await apiService.getCountriesWithLaws()

        if (countries && Array.isArray(countries) && countries.length > 0) {
          if (!availableCountries || availableCountries.length === 0) {
            setAvailableCountries(countries)
          }

          // Validate country from URL
          const urlCountryExists = countries.some(
            (c) => c.countryName?.toLowerCase() === urlCountry?.toLowerCase()
          )

          const normalizedCountry = countries.find(
            (c) => c.countryName?.toLowerCase() === urlCountry?.toLowerCase()
          )?.countryName

          if (urlCountryExists) {
            setPrimaryCountry(normalizedCountry)
          } else if (countries.length > 0) {
            // If URL country doesn't exist, fall back to the first available country.
            // In embedded (admin) mode we don't own the URL, so just set state.
            const defaultCountry = countries[0].countryName
            setPrimaryCountry(defaultCountry)
            if (!useExactSearch) {
              navigate(`/search/${defaultCountry}/Tax-Treaties`)
              return
            }
          }

          // Keep URL-driven partner/query values normalized in local state
          const normalizedPartnerCountry = partnerCountryParam || ''
          const normalizedQuery = queryParam || ''
          setSelectedCountry(normalizedPartnerCountry)
          setSearchQuery(normalizedQuery)
          setHasSearched(Boolean(normalizedQuery))

          // Always load treaty results for the resolved country scope on initialization.
          const requestPrimaryCountry = normalizedCountry || urlCountry || ''
          if (requestPrimaryCountry) {
            const requestKey = `${requestPrimaryCountry.toLowerCase()}|${normalizedPartnerCountry.toLowerCase()}|${normalizedQuery.trim().toLowerCase()}`
            const existingRequestKey = searchRequestRef.current
              ? `${(searchRequestRef.current.primaryCountry || '').toLowerCase()}|${(searchRequestRef.current.partnerCountry || '').toLowerCase()}|${(searchRequestRef.current.searchQuery || '').trim().toLowerCase()}`
              : null

            // Set up search request only when it differs from the in-flight/current one.
            if (requestKey !== existingRequestKey) {
              const currentRequest = {
                primaryCountry: requestPrimaryCountry,
                partnerCountry: normalizedPartnerCountry,
                searchQuery: normalizedQuery,
                timestamp: Date.now(),
              }

              searchRequestRef.current = currentRequest
              lastFetchTimestamp.current = currentRequest.timestamp

              // Trigger a search
              setLoading(true)
            }
          }

          setError(null)
        } else {
          setError('No countries available')
        }
      } catch (error) {
        console.error('Error initializing component:', error)
        setError('Failed to load countries. Please try again later.')
      } finally {
        setIsInitializing(false)
        setTimeout(() => {
          hasSettledInitializationRef.current = true
        }, 0)
      }
    }

    initializeComponent()
  }, [urlCountry, partnerCountryParam, queryParam, navigate, availableCountries, useExactSearch])

  /**
   * Fetch tax treaties data
   */
  useEffect(() => {
    const fetchData = async () => {
      // Skip if initializing, no primary country, or not in loading state
      if (isInitializing || !primaryCountry || !loading) {
        return
      }

      // Get current search request
      const currentRequest = searchRequestRef.current
      if (!currentRequest) {
        setLoading(false)
        return
      }

      // Skip if this is an outdated request (a newer one has been made)
      if (
        currentRequest.timestamp < lastFetchTimestamp.current &&
        currentRequest.timestamp !== lastFetchTimestamp.current
      ) {
        setLoading(false)
        return
      }

      setError(null)

      try {
        const isComplexQuery = /[\[\]\(\){}^$<>]/.test(
          currentRequest.searchQuery || ''
        )
        // When there's a search query, fetch ALL results and let client handle pagination, categorization of direct matches otherwise use server-side pagination
        const hasSearchQuery =
          currentRequest.searchQuery &&
          currentRequest.searchQuery.trim().length > 0
        // Prepare API request parameters
        const params = {
          country1: currentRequest.primaryCountry,
          country2: currentRequest.partnerCountry || '',
          query: isComplexQuery
            ? btoa(
                unescape(encodeURIComponent(currentRequest.searchQuery || ''))
              )
            : currentRequest.searchQuery || '',
          isEncodedQuery: isComplexQuery ? 'true' : 'false',
          page: hasSearchQuery ? 1 : currentPage,
          pageSize: hasSearchQuery ? 9999 : itemsPerPage, // Fetch all when searching
        }

        // Make API call - use V1 (exact-search) when caller opts in, otherwise V2
        const response = useExactSearch
          ? await apiService.searchTaxTreatiesV1(params, {
              cancelPrevious: true,
              abortKey: `tax-treaty-search-v1:${currentRequest.primaryCountry}`,
            })
          : await apiService.searchTaxTreatiesV2(params, {
              cancelPrevious: true,
              abortKey: `tax-treaty-search-v2:${currentRequest.primaryCountry}`,
            })

        // Handle pagination edge case
        if (
          response.content?.length === 0 &&
          currentPage > 1 &&
          !hasSearchQuery
        ) {
          setSnackbarMessage('Requested page is not available.')
          setSnackbarOpen(true)
          setCurrentPage(1)
          return
        }
        if (hasSearchQuery) {
          // Store ALL results for client-side categorization
          setAllSearchResults(response.content || [])
          setTotalItems(response.totalElements || 0)
          const startIndex = (currentPage - 1) * itemsPerPage
          const endIndex = startIndex + itemsPerPage

          setCurrentPageData((response.content || []).slice(startIndex, endIndex))
        } else {
          // Normal pagination - no search query
          setAllSearchResults([])
          // Update state with results
          setTotalItems(response.totalElements || 0)
          setCurrentPageData(response.content || [])
        }
        // Show the "no results" popup only after an authenticated user
        // actively searched with a query and got zero treaties back.
        const shouldShowNoResults =
          (response.totalElements || 0) === 0 &&
          hasSearchQuery &&
          Boolean(isAuthenticated)
        if (shouldShowNoResults) {
          const scopeLabel = currentRequest.partnerCountry
            ? `${currentRequest.primaryCountry} Tax Treaties with ${currentRequest.partnerCountry}`
            : `${currentRequest.primaryCountry} Tax Treaties`
          setNoResultsSnapshot({
            query: currentRequest.searchQuery || '',
            scopeName: scopeLabel,
          })
        }
        setShowNoResultsModal(shouldShowNoResults)
        if (isAuthenticated) {
          // if (isAuthenticated && currentRequest.searchQuery) { // if authenticated AND has searchQuery
          const treatiesCount = response.totalElements || 0
          activityTracker.trackSearchInteraction(
            'tax-treaties', // searchType
            currentRequest.searchQuery, // query
            {
              // filters
              country1: currentRequest.primaryCountry,
              country2: currentRequest.partnerCountry || undefined,
            },
            treatiesCount, // resultsCount
            { treaties: treatiesCount }, // breakdown
            {
              selectedSuggestion: currentRequest.selectedSuggestion,
            },
          )
        }
      } catch (error) {
        if (apiService.isRequestCanceled(error) || error?.isCanceled) {
          return
        }
        console.error('Error fetching tax treaties:', error)
        const errorMessage =
          error.userMessage ||
          'Failed to load tax treaties. Please try again later.'
        setError(errorMessage)
        setTotalItems(0)
        setCurrentPageData([])
        setAllSearchResults([])
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [
    loading,
    primaryCountry,
    currentPage,
    itemsPerPage,
    isInitializing,
    isAuthenticated,
    useExactSearch,
  ])

  /**
   * Handle pagination
   */
  const paginate = useCallback(
    (pageNumber) => {
      if (pageNumber < 1 || pageNumber > Math.ceil(totalItems / itemsPerPage)) {
        setSnackbarMessage('Requested page is not available.')
        setSnackbarOpen(true)
        return
      }

      if (isAuthenticated) {
        activityTracker.trackCustomEvent('tax_treaty_page_changed', {
          from: currentPage,
          to: pageNumber,
          totalItems,
          itemsPerPage,
        })
      }

      setCurrentPage(pageNumber)

      // Only trigger loading if we DON'T have all results cached
      if (allSearchResults.length === 0) {
        setLoading(true)
      }

      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    [
      totalItems,
      itemsPerPage,
      currentPage,
      isAuthenticated,
      allSearchResults.length,
    ]
  )
  const handleShowSnackbar = useCallback((message, severity = 'info') => {
    setSnackbarMessage(message)
    // setSnackbarSeverity(severity)
    setSnackbarOpen(true)
  }, [])
  /**
   * Close snackbar
   */
  const handleSnackbarClose = useCallback((event, reason) => {
    if (reason === 'clickaway') return
    setSnackbarOpen(false)
  }, [])

  /**
   * Scroll to top function
   */
  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }, [])

  /**
   * Show/hide back to top button based on scroll position
   */
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 50)
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  /**
   * Toggle filter visibility (for mobile)
   */
  const toggleFilters = useCallback(() => {
    setShowFilters((prev) => !prev)
  }, [])

  /**
   * Prepare sidebar props
   */
  const sideBarProps = useMemo(
    () => ({
      primaryCountry,
      setPrimaryCountry,
      selectedCountry,
      setSelectedCountry,
      searchQuery,
      setSearchQuery,

      // Data sources
      availableCountries,

      // UI state
      loading: loading || isInitializing,
      error,
      isAuthenticated,

      // Handlers
      handleReset,
      handleSearch,

      // Navigation and state reset
      navigate,
      setCurrentPage,
      setCurrentPageData,
      setTotalItems,
      useExactSearch,
      autocompleteEnabled,
    }),
    [
      primaryCountry,
      setPrimaryCountry,
      selectedCountry,
      setSelectedCountry,
      searchQuery,
      setSearchQuery,
      availableCountries,
      loading,
      isInitializing,
      error,
      isAuthenticated,
      handleReset,
      handleSearch,
      navigate,
      setCurrentPage,
      setCurrentPageData,
      setTotalItems,
      useExactSearch,
      autocompleteEnabled,
    ]
  )

  /**
   * Render active filters section
   */
  const renderActiveFilters = useCallback(() => {
    if (!primaryCountry) return null

    // Handle chip deletion
    const handleChipDelete = (filterType, value) => {
      if (filterType === 'partner') {
        setSelectedCountry('')
        setPendingFilterChanges(true)
      } else if (filterType === 'query') {
        setSearchQuery('')
        setHasSearched(false)
        setPendingFilterChanges(true)
      }
    }

    return (
      <Box sx={{ mt: 2, mb: 3 }}>
        <Typography variant='subtitle1' sx={{ mb: 1, fontWeight: 500 }}>
          Active Filters:
        </Typography>
        <Stack direction='row' spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Chip
            label={`Country 1: ${primaryCountry}`}
            color='default'
            size='medium'
            // Primary country cannot be deleted as it's required
          />

          {selectedCountry ? (
            <Chip
              label={`Country 2: ${selectedCountry}`}
              color='primary'
              size='medium'
              onDelete={() => handleChipDelete('partner', selectedCountry)}
              sx={{
                bgcolor: themeColors.primary,
                color: '#fff',
                '& .MuiChip-deleteIcon': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&:hover': { color: '#fff' },
                },
              }}
            />
          ) : (
            <Chip
              label='Country 2: All Partners'
              color='primary'
              size='medium'
              sx={{ bgcolor: themeColors.primary, color: '#fff' }}
            />
          )}

          {searchQuery && hasSearched && (
            <Chip
              label={`Search: "${searchQuery}"`}
              color='info'
              size='medium'
              onDelete={() => handleChipDelete('query', searchQuery)}
            />
          )}
        </Stack>
      </Box>
    )
  }, [primaryCountry, selectedCountry, searchQuery, themeColors, hasSearched])

  // Handle pagination when we already have all search results
  useEffect(() => {
    if (allSearchResults.length > 0 && searchRequestRef.current?.searchQuery) {
      const startIndex = (currentPage - 1) * itemsPerPage
      const endIndex = startIndex + itemsPerPage
      setCurrentPageData(allSearchResults.slice(startIndex, endIndex))
    }
  }, [currentPage, itemsPerPage, allSearchResults])
  /**
   * Render treaty results (V2 backend already returns them in ranked order).
   */
  const renderTreatyResults = useCallback(
    (data) => {
      if (data.length === 0) {
        return <Alert severity='info'>No DTAAs to display.</Alert>
      }

      return (
        <Box>
          {data.map((treaty) => (
            <TaxTreatyCard
              key={treaty.slug}
              treaty={treaty}
              searchQuery={searchQuery}
              showSnackbar={handleShowSnackbar}
            />
          ))}
        </Box>
      )
    },
    [searchQuery, handleShowSnackbar]
  )

  /**
   * Render content function with optimized loading
   */
  const renderContent = useCallback(() => {
    // When initializing the component
    if (isInitializing) {
      return (
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          height='200px'
        >
          <CircularProgress sx={{ color: themeColors.primary }} />
        </Box>
      )
    }

    if (error) {
      return (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )
    }

    if (!primaryCountry) {
      return (
        <Alert severity='info' sx={{ mb: 2 }}>
          Please select a country to view its tax treaties.
        </Alert>
      )
    }

    if (currentPageData.length === 0 && !loading) {
      return (
        <Alert severity='info' sx={{ mb: 2 }}>
          {`No tax treaties found for ${primaryCountry}${
            selectedCountry ? ` with ${selectedCountry}` : ''
          }`}
          {searchQuery ? ` containing "${searchQuery}"` : ''}.
        </Alert>
      )
    }

    return (
      <Box sx={{ position: 'relative', minHeight: '200px' }}>
        {/* Loading overlay - keeps content visible but shows loading indicator */}
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255,255,255,0.7)',
              zIndex: 5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={40} sx={{ color: themeColors.primary }} />
          </Box>
        )}

        {/* Search result summary */}
        {currentPageData.length > 0 && (
          <Typography variant='subtitle1' sx={{ mb: 2, color: '#00796b' }}>
            Found{' '}
            <Box component='span' sx={{ fontWeight: 700 }}>
              {totalItems} tax treat{totalItems === 1 ? 'y' : 'ies'}
            </Box>
            {selectedCountry ? ` with ${selectedCountry}` : ''}
            {searchQuery ? ` containing "${searchQuery}"` : ''}
          </Typography>
        )}

        {/* Treaty list */}
        <Paper sx={{ width: '100%', p: 2, borderRadius: '8px' }} elevation={1}>
          <Box sx={{ mb: 3 }}>
            {/* {currentPageData.map((treaty) => (
            <TaxTreatyCard
              key={treaty.slug || treaty.id}
              treaty={treaty}
              searchQuery={searchQuery}
            />
          ))} */}
            {renderTreatyResults(currentPageData)}
          </Box>

          {/* Pagination */}
          {currentPageData.length > 0 && totalItems > itemsPerPage && (
            <MemoizedPagination
              itemsPerPage={itemsPerPage}
              totalItems={totalItems}
              currentPage={currentPage}
              paginate={paginate}
            />
          )}
        </Paper>
        {/* Items per page selector */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <ItemsPerPageSelector
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            loading={loading}
            themeColors={themeColors}
          />
        </Box>
      </Box>
    )
  }, [
    isInitializing,
    error,
    primaryCountry,
    currentPageData,
    loading,
    totalItems,
    selectedCountry,
    searchQuery,
    itemsPerPage,
    themeColors,
    currentPage,
    paginate,
    renderTreatyResults,
  ])

  return (
    <Container maxWidth='lg' sx={{ py: 3 }}>
      {/* Breadcrumbs navigation */}
      <Breadcrumbs
        aria-label='breadcrumb'
        sx={{ mb: 2 }}
        separator={<NavigateNextIcon fontSize='small' />}
      >
        <Link
          color='inherit'
          href='/'
          sx={{
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          Home
        </Link>
        {/* <Link
          color='inherit'
          href='/search-across-country'
          sx={{
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          Search across Law
        </Link> */}
        <Typography color='text.primary'>Tax Treaties (DTAA)</Typography>
      </Breadcrumbs>

      <Grid container spacing={3}>
        {isSmallScreen ? (
          // Mobile layout - stacked
          <>
            {/* Filter toggle button for mobile */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant='h5' component='h1'>
                  {primaryCountry
                    ? `${primaryCountry} Tax Treaties (DTAA)`
                    : 'Tax Treaties (DTAA)'}
                </Typography>
                <Button
                  variant='contained'
                  color='primary'
                  startIcon={
                    showFilters ? <FilterAltOffIcon /> : <FilterAltIcon />
                  }
                  onClick={toggleFilters}
                  sx={{
                    bgcolor: themeColors.primary,
                    color: '#fff',
                    '&:hover': {
                      bgcolor: themeColors.hover,
                    },
                  }}
                >
                  {showFilters ? 'Hide Filters' : 'Show Filters'}
                </Button>
              </Box>
            </Grid>

            {/* Collapsible sidebar */}
            {showFilters && (
              <Grid item xs={12}>
                <Paper elevation={2} sx={{ borderRadius: '8px' }}>
                  <TaxTreatySideBar {...sideBarProps} />
                </Paper>
              </Grid>
            )}

            {/* Active filters display */}
            <Grid item xs={12}>
              {renderActiveFilters()}
            </Grid>

            {/* Content */}
            <Grid item xs={12}>
              {renderContent()}
            </Grid>
          </>
        ) : (
          // Desktop layout with sticky sidebar
          <>
            <Grid item md={4} lg={3} style={{ position: 'relative' }}>
              {/* Sticky wrapper with improved spacing */}
              <div
                style={{
                  position: 'sticky',
                  top: '2rem',
                  maxHeight: 'calc(100vh - 4rem)',
                  paddingBottom: '1rem',
                  overflowY: 'auto',
                }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    borderRadius: '8px',
                    transition: 'box-shadow 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    },
                  }}
                >
                  <TaxTreatySideBar {...sideBarProps} />
                </Paper>
              </div>
            </Grid>
            <Grid item md={8} lg={9}>
              <Typography variant='h5' component='h1' sx={{ mb: 3 }}>
                {primaryCountry
                  ? `${primaryCountry} Tax Treaties (DTAA)`
                  : 'Tax Treaties (DTAA)'}
              </Typography>

              {renderActiveFilters()}
              {renderContent()}
            </Grid>
          </>
        )}
      </Grid>

      {/* Back to Top button */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1000,
        }}
      >
        <Zoom in={showBackToTop}>
          {isSmallScreen ? (
            // Small circular Fab for mobile
            <Fab
              size='small'
              aria-label='Scroll back to top'
              onClick={scrollToTop}
              sx={{
                backgroundColor: themeColors.secondary,
                color: themeColors.primary,
                '&:hover': {
                  backgroundColor: 'white',
                  color: themeColors.primary,
                  fontWeight: 'bold',
                },
                '&:active': {
                  backgroundColor: themeColors.secondary,
                  color: themeColors.primary,
                },
                paddingLeft: 2,
                paddingRight: 2,
              }}
            >
              <KeyboardArrowUpIcon fontSize='small' />
            </Fab>
          ) : (
            <Fab
              variant='extended'
              size='medium'
              aria-label='Scroll back to top'
              onClick={scrollToTop}
              sx={{
                backgroundColor: themeColors.secondary,
                color: themeColors.primary,
                '&:hover': {
                  backgroundColor: 'white',
                  color: themeColors.primary,
                  fontWeight: 'bold',
                },
                '&:active': {
                  backgroundColor: themeColors.secondary,
                  color: themeColors.primary,
                },
                paddingLeft: 2,
                paddingRight: 2,
                borderRadius: buttonBorderRadius,
              }}
            >
              <KeyboardArrowUpIcon sx={{ mr: 1 }} />
              <Typography variant='button' sx={{ fontWeight: 'bold' }}>
                Back to Top
              </Typography>
            </Fab>
          )}
        </Zoom>
      </Box>

      {/* Notification snackbar */}
      <CustomSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        autoHideDuration={1500}
      />
      <NoResultsModal
        open={showNoResultsModal}
        onClose={() => setShowNoResultsModal(false)}
        username={user?.name || user?.username || 'User'}
        searchQuery={noResultsSnapshot.query}
        scopeName={noResultsSnapshot.scopeName}
        scopeNoun='partner country'
      />
    </Container>
  )
}

export default TaxTreatyList
